import { CircleAlert, LucideIcon } from 'lucide-react'
import { ComponentProps, ReactNode } from 'react'

import { Signal } from '@/gen/inventory/v1/signal_pb'

import { formatTimestamp } from '@/lib/date/date'

import { Alert, AlertIcon } from '@/components/lemmala-design-system/alert/alert'
import { Badge } from '@/components/lemmala-design-system/badge/badge'

type SignalIndicator = {
  key: string
  isActive: (signal: Signal) => boolean
  // Compact chip shown beside the signal title in tables.
  badge?: {
    label: string
    variant: ComponentProps<typeof Badge>['variant']
    className?: string
  }
  // Full-width alert bar shown in the signal detail view.
  banner?: {
    variant: ComponentProps<typeof Alert>['variant']
    icon: LucideIcon
    title: string
    description?: (signal: Signal) => ReactNode
  }
}

// Declarative registry mapping a signal's domain state to the UI indicators that
// surface it. To add an indicator for any signal condition, add one entry here:
// the shared <SignalBadges/> and <SignalBanners/> render whatever applies, so no
// per-signal-type branching ever leaks into table cells or the detail view.
const SIGNAL_INDICATORS: SignalIndicator[] = [
  {
    key: 'cisa-kev',
    isActive: (signal) => signal.knownExploited,
    // Figma uses background/subtle/gray specifically (lighter than neutral's default fill).
    badge: { label: 'CISA KEV', variant: 'neutral', className: 'bg-main-background-subtle-gray' },
    banner: {
      variant: 'destructive',
      icon: CircleAlert,
      title: 'Known exploitation [CISA KEV]',
      description: (signal) => {
        const dueDate = formatTimestamp(signal.kevDueDate, 'monthDayYearComma')
        return dueDate && `Remediation due ${dueDate}`
      },
    },
  },
]

export const SignalBadges = ({ signal }: { signal: Signal }) => {
  const active = SIGNAL_INDICATORS.flatMap((indicator) =>
    indicator.badge && indicator.isActive(signal)
      ? [{ key: indicator.key, badge: indicator.badge }]
      : [],
  )
  if (active.length === 0) {
    return null
  }

  return (
    <>
      {active.map(({ key, badge }) => (
        <Badge
          key={key}
          variant={badge.variant}
          badgeStyle='subtle'
          size='default'
          className={badge.className}
        >
          {badge.label}
        </Badge>
      ))}
    </>
  )
}

export const SignalBanners = ({ signal }: { signal: Signal }) => {
  const active = SIGNAL_INDICATORS.flatMap((indicator) =>
    indicator.banner && indicator.isActive(signal)
      ? [{ key: indicator.key, banner: indicator.banner }]
      : [],
  )
  if (active.length === 0) {
    return null
  }

  return (
    <>
      {active.map(({ key, banner }) => (
        <Alert
          key={key}
          variant={banner.variant}
          icon={<AlertIcon icon={banner.icon} />}
          alertTitle={banner.title}
          description={banner.description?.(signal)}
        />
      ))}
    </>
  )
}
